import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { storageAdapter } from '@bettercampus/global/runtime';
import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { enablePushSubscription, getPwaPushApiBase } from '@/lib/pwa-push';

const CODE_LENGTH = 8;
type VerifyState = 'idle' | 'verifying' | 'error' | 'success';

export function VerifyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCode = searchParams.get('code') ?? '';
  const [code, setCode] = useState(initialCode);
  const [state, setState] = useState<VerifyState>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    storageAdapter.get('local', ['auth']).then(result => {
      if (result?.auth?.access_token) {
        setState('success');
      }
    });
  }, []);

  useEffect(() => {
    if (state !== 'success') return;
    const timer = setTimeout(() => navigate('/notifications', { replace: true }), 2000);
    return () => clearTimeout(timer);
  }, [state, navigate]);

  const verify = useCallback(
    async (value: string) => {
      const apiBase = getPwaPushApiBase();
      if (!apiBase) {
        setState('error');
        setErrorMsg('App configuration error. Please try again later.');
        return;
      }

      setState('verifying');
      setErrorMsg('');

      try {
        const res = await fetch(`${apiBase}/users/verify-qr-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: value.toLowerCase() }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            (body as { error?: string }).error || 'Verification failed. Please try again.'
          );
        }

        const { message, error } = await res.json();

        if (error) throw new Error(error);

        await storageAdapter.set('local', { auth: message });
        setState('success');

        // Set up push notifications in the background — don't block the success flow
        enablePushSubscription(apiBase)
          .then(pushError => {
            if (pushError) {
              toast.error('Notifications unavailable', { description: pushError });
            }
          })
          .catch(e => {
            const msg = e instanceof Error ? e.message : 'Failed to enable push notifications';
            console.error('[pwa] ensurePushSubscription failed:', e);
            toast.error('Notifications unavailable', { description: msg });
          });
      } catch (e) {
        setState('error');
        setErrorMsg(e instanceof Error ? e.message : 'Verification failed');
        hasSubmittedRef.current = false;
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (code.length === CODE_LENGTH && !hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      verify(code);
    }
  }, [code, verify]);

  if (state === 'success') {
    return (
      <div
        className="app-shell"
        style={{ flexDirection: 'column', justifyContent: 'flex-start', paddingTop: '3rem' }}
      >
        <div className="install-card">
          <img src="/icon-192.png" alt="" className="install-logo" width={96} height={96} />
          <h1 className="install-title" style={{ color: '#16a34a' }}>
            Success
          </h1>
          <p className="install-description">Redirecting to notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="app-shell"
      style={{ flexDirection: 'column', justifyContent: 'flex-start', paddingTop: '3rem' }}
    >
      <div className="install-card">
        <img src="/icon-192.png" alt="" className="install-logo" width={96} height={96} />
        <h1 className="install-title">Verify Your Account</h1>
        <p className="install-description">
          Enter the 8-character code shown in the BetterCampus extension.
        </p>

        <div className="flex justify-center">
          <InputOTP
            maxLength={CODE_LENGTH}
            pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
            value={code}
            onChange={val => {
              setCode(val);
              if (state === 'error') {
                setState('idle');
                setErrorMsg('');
              }
            }}
            disabled={state === 'verifying'}
            autoFocus
          >
            <InputOTPGroup>
              {Array.from({ length: CODE_LENGTH }, (_, i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>

        {state === 'verifying' && (
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>Verifying...</p>
        )}

        {state === 'error' && (
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#ef4444' }}>
            {errorMsg || 'Verification failed. Please try again.'}
          </p>
        )}
      </div>
    </div>
  );
}

export default VerifyPage;
